import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LoginSignupCard } from '../components/auth/LoginSignupCard';

export const Home: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Redirect authenticated users to marketplace
  if (!authLoading && isAuthenticated) {
    return <Navigate to="/marketplace" replace />;
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          {/* Fallback gradient background - behind video */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-0"></div>
          
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover z-10"
            onLoadStart={() => {
              console.log('Video loading started');
            }}
            onCanPlay={() => {
              console.log('Video can play');
            }}
            onLoadedData={() => {
              console.log('Video loaded successfully');
            }}
            onError={(e) => {
              console.error('Video failed to load:', e);
              console.error('Video src:', (e.target as HTMLVideoElement).src);
              console.error('Video error code:', (e.target as HTMLVideoElement).error?.code);
              console.error('Video error message:', (e.target as HTMLVideoElement).error?.message);
              // Hide video on error and show fallback background
              (e.target as HTMLVideoElement).style.display = 'none';
            }}
          >
            <source src="/Videos/blackground.mp4" type="video/mp4" />
            <source src="./Videos/blackground.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Dark overlay for text readability - on top of video */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/60 z-20"></div>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-30 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-balance">
            Deconstructed
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-balance opacity-90">
            Turn demolition into opportunity. List, sell, and discover sustainable building materials.
          </p>
          
          {/* Call to Action Buttons */}
          <div className="space-y-4">
            <p className="text-lg mb-6 opacity-80">
              Join our community of builders, makers, and sustainability enthusiasts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <button
                onClick={() => setShowLoginModal(true)}
                className="btn-primary text-lg px-8 py-4 flex-1"
              >
                Get Started
              </button>
              <button
                onClick={() => setShowLoginModal(true)}
                className="bg-white/20 text-white border border-white/30 hover:bg-white/30 transition-colors duration-200 text-lg px-8 py-4 rounded-lg font-semibold flex-1"
              >
                Sign In
              </button>
            </div>
            <p className="text-white/70 text-sm mt-4">
              No password required - we'll send you a verification code
            </p>
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <div className="py-16 px-4 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Why Choose Deconstructed?
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Transform waste into opportunity with our sustainable marketplace for building materials.
            </p>
          </div>
          
          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Sustainable Impact</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Reduce waste and environmental impact by giving building materials a second life.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Cost Effective</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Find quality building materials at fraction of retail prices while supporting sustainability.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Community Driven</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Connect with builders, makers, and sustainability enthusiasts in your area.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <button 
              onClick={() => setShowLoginModal(true)}
              className="btn-primary text-lg px-8 py-4"
            >
              Join Our Community
            </button>
          </div>
        </div>
      </div>

      {/* Login/Signup Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowLoginModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative z-10 w-full max-w-md">
            <LoginSignupCard 
              onClose={() => setShowLoginModal(false)}
              className="shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};
